
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, RefreshCw } from 'lucide-react';
import { pricingConfig } from '@/hooks/subscription/types';
import { usePaymentRouting } from '@/hooks/subscription/usePaymentRouting';
import { isIOSNativeApp } from '@/utils/platformUtils';
import { handleIOSIAPError } from '@/utils/iosIAPErrorHandler';
import { useState, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';

interface SubscriptionPlansProps {
  billingPeriod: 'monthly' | 'annual';
  onSelectPlan: (priceId: string) => void;
  isLoading: boolean;
}

/**
 * Enhanced Subscription Plans Component
 *
 * PHASE 3.1: Paywall Enhancement with iOS IAP Integration
 *
 * This component provides a unified subscription interface that automatically
 * adapts based on the user's platform:
 *
 * - iOS Native App: Shows iOS IAP purchase options with App Store integration
 * - Android/Web: Shows existing Stripe payment interface
 *
 * Features:
 * - Platform-conditional rendering for payment methods
 * - iOS-specific "Restore Purchases" functionality
 * - Unified pricing display across all platforms
 * - Enhanced error handling for iOS IAP scenarios
 * - Seamless integration with PaymentRoutingService
 */
export function SubscriptionPlans({ billingPeriod, onSelectPlan, isLoading }: SubscriptionPlansProps) {
  // Platform detection
  const isIOSNative = isIOSNativeApp();

  // Payment routing for iOS IAP integration
  const {
    paymentMethod,
    isProcessing: isPaymentProcessing,
    error: paymentError,
    initiatePayment,
    checkIOSIAPAvailability,
    resetState
  } = usePaymentRouting();

  // Local state for iOS-specific features
  const [isRestoringPurchases, setIsRestoringPurchases] = useState(false);
  const [iosIAPAvailable, setIOSIAPAvailable] = useState<boolean | null>(null);

  // Check iOS IAP availability on mount for iOS devices
  useEffect(() => {
    if (isIOSNative) {
      const checkAvailability = async () => {
        try {
          const available = await checkIOSIAPAvailability();
          setIOSIAPAvailable(available);
          console.log('📱 SubscriptionPlans: iOS IAP availability:', available);
        } catch (error) {
          console.error('❌ SubscriptionPlans: Failed to check iOS IAP availability:', error);
          setIOSIAPAvailable(false);
          // Use centralized error handling for availability check
          handleIOSIAPError(error instanceof Error ? error : String(error), 'iOS IAP Availability Check');
        }
      };

      checkAvailability();
    }
  }, [isIOSNative, checkIOSIAPAvailability]);

  // Use the same pricing configuration as the landing page
  const PRICING_CONFIG = {
    monthly: {
      price: pricingConfig.monthly.formattedPrice,
      billing: 'per month',
      priceId: 'monthly' // This gets passed to the backend which has the real Stripe price ID
    },
    annual: {
      // Apple requires the actual billed amount to be most prominent
      price: pricingConfig.annual.formattedPrice, // $179.00 - the actual billed amount
      billing: 'billed annually',
      monthlyEquivalent: `$${(pricingConfig.annual.amount / 100 / 12).toFixed(2)}/month`, // $14.92/month - subordinate
      priceId: 'annual', // This gets passed to the backend which has the real Stripe price ID
      savingsAmount: `$${((pricingConfig.monthly.amount * 12 - pricingConfig.annual.amount) / 100).toFixed(0)}`
    }
  };

  const features = [
    'Unlimited training session logging',
    'Advanced progress analytics',
    'Personalized AI insights',
    'Competition tracking',
    'Technique recommendations',
    'Priority customer support',
    'Export your data'
  ];

  const currentPlan = PRICING_CONFIG[billingPeriod];
  const price = currentPlan.price;
  const billing = currentPlan.billing;
  const priceId = currentPlan.priceId;

  // Enhanced purchase handling with platform routing
  const handlePurchase = async () => {
    console.log('🛒 SubscriptionPlans: Purchase initiated for:', { billingPeriod, paymentMethod });

    // Reset any previous payment errors
    resetState();

    if (isIOSNative && paymentMethod === 'ios_iap') {
      // Use iOS IAP through payment routing
      try {
        const result = await initiatePayment(billingPeriod);

        if (result.success) {
          console.log('✅ SubscriptionPlans: iOS IAP purchase initiated successfully');
          toast({
            title: 'Purchase Initiated',
            description: 'Your purchase is being processed through the App Store.',
          });
        } else {
          console.error('❌ SubscriptionPlans: iOS IAP purchase failed:', result.error);
          // Use centralized error handling for iOS IAP errors
          if (result.error) {
            handleIOSIAPError(result.error, 'iOS IAP Purchase');
          } else {
            toast({
              title: 'Purchase Failed',
              description: 'Failed to initiate purchase. Please try again.',
              variant: 'destructive'
            });
          }
        }
      } catch (error) {
        console.error('❌ SubscriptionPlans: iOS IAP purchase error:', error);
        // Use centralized error handling for iOS IAP errors
        handleIOSIAPError(error instanceof Error ? error : String(error), 'iOS IAP Purchase');
      }
    } else {
      // Use existing Stripe flow for non-iOS platforms
      onSelectPlan(priceId);
    }
  };

  // iOS-specific restore purchases functionality
  const handleRestorePurchases = async () => {
    if (!isIOSNative) {
      console.log('🚫 SubscriptionPlans: Restore purchases only available on iOS');
      return;
    }

    setIsRestoringPurchases(true);

    try {
      console.log('🔄 SubscriptionPlans: Restoring iOS purchases...');

      // Import iOS IAP service dynamically to avoid issues on other platforms
      const { iosIAPService } = await import('@/services/iap/IOSInAppPurchaseService');

      const result = await iosIAPService.restorePurchases();

      if (result) {
        toast({
          title: 'Purchases Restored',
          description: 'Your previous purchases have been restored successfully.',
        });
        console.log('✅ SubscriptionPlans: Purchases restored successfully');
      } else {
        toast({
          title: 'No Purchases Found',
          description: 'No previous purchases were found to restore.',
          variant: 'default'
        });
        console.log('ℹ️ SubscriptionPlans: No purchases to restore');
      }
    } catch (error) {
      console.error('❌ SubscriptionPlans: Restore purchases failed:', error);
      // Use centralized error handling for iOS IAP errors
      handleIOSIAPError(error instanceof Error ? error : String(error), 'iOS IAP Restore Purchases');
    } finally {
      setIsRestoringPurchases(false);
    }
  };

  // Determine loading state (combines existing loading with payment processing)
  const isButtonLoading = isLoading || isPaymentProcessing;

  // Determine if purchase button should be disabled
  const isPurchaseDisabled = isButtonLoading || (isIOSNative && iosIAPAvailable === false);

  return (
    <div className="max-w-md mx-auto">
      <Card className="border-2 border-primary">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <CardTitle className="text-2xl">Premium Plan</CardTitle>
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              Most Popular
            </Badge>
          </div>
          <div className="text-4xl font-bold">{price}</div>
          <div className="text-muted-foreground">{billing}</div>
          {billingPeriod === 'annual' && (
            <>
              {/* Monthly equivalent shown in subordinate position per Apple guidelines */}
              <div className="text-sm text-muted-foreground mt-1">
                {PRICING_CONFIG.annual.monthlyEquivalent}
              </div>
              <Badge variant="secondary" className="mt-2 bg-orange-100 text-orange-800">
                Save {PRICING_CONFIG.annual.savingsAmount}/year
              </Badge>
            </>
          )}
        </CardHeader>
        <CardContent>
          {/* Enhanced purchase button with platform-specific handling */}
          <Button
            onClick={handlePurchase}
            disabled={isPurchaseDisabled}
            size="lg"
            className="w-full mb-6"
          >
            {isButtonLoading ? 'Processing...' : 'Upgrade Now'}
          </Button>

          {/* iOS IAP availability warning */}
          {isIOSNative && iosIAPAvailable === false && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                App Store purchases are not available. Please check your device settings or try again later.
              </p>
            </div>
          )}

          {/* Payment error display */}
          {paymentError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">
                {paymentError}
              </p>
            </div>
          )}

          {/* Platform-specific payment method indicator */}
          {isIOSNative && iosIAPAvailable && (
            <div className="mb-4 text-center">
              <Badge variant="outline" className="text-xs">
                🍎 App Store Purchase
              </Badge>
            </div>
          )}
          
          <div className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>


          {/* iOS-specific Restore Purchases functionality */}
          {isIOSNative && (
            <div className="mt-4 text-center">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 underline disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
                onClick={handleRestorePurchases}
                disabled={isRestoringPurchases || iosIAPAvailable === false}
              >
                {isRestoringPurchases && <RefreshCw className="h-3 w-3 animate-spin" />}
                {isRestoringPurchases ? 'Restoring...' : 'Restore Purchases'}
              </button>
              <p className="text-xs text-muted-foreground mt-1">
                Restore previous App Store purchases
              </p>
            </div>
          )}

          {/* Required functional links for Apple App Store compliance */}
          {isIOSNative && (
            <div className="mt-4 flex justify-center space-x-4">
              <button
                onClick={() => window.open('https://thematmonster.com/legal#privacy', '_system')}
                className="text-xs text-primary underline hover:text-primary/80"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => window.open('https://www.apple.com/legal/internet-services/itunes/dev/stdeula/', '_system')}
                className="text-xs text-primary underline hover:text-primary/80"
              >
                Terms of Use
              </button>
            </div>
          )}

          {/* Platform-specific terms and conditions */}
          <div className="mt-4 text-xs text-muted-foreground text-center">
            {isIOSNative ? (
              <>
                Payment will be charged to your Apple ID account at confirmation of purchase.
                <br />
                Subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current period.
                <br />
                Manage your subscription in your App Store account settings.
              </>
            ) : (
              '60 day money-back guarantee • Cancel anytime • No commitment'
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
